import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import HomeIcon from "assets/icons/HomeIcon";
import { SupportedLang } from "config/constants";
import { USER_MANUAL_LINKS } from "config/userManualLinks";
import useAllClasses from "hooks/useAllClasses";
import {
  ACCOUNT_PATH,
  BASE_PATH,
  CHANGE_PASSWORD_PATH,
  CLASS_PATH,
  FACILITY_PATH,
  FAQ_PATH,
  PARENTS_MATERIAL_PATH,
  PICTURES_PATH,
  PICTURES_RESULTS_PATH,
  PICTURES_SUMMARIZATION_PATH,
  SETTING_PATH,
  SYSTEM_INFO_PATH,
  UNPUBLISHED_PICTURES_PATH,
  UPLOAD_PATH,
  USER_MANUAL_PATH,
} from "router/pathName";
import { NEW, STUDENT } from "config/keywords";

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbsProps {
  fallbackTitle?: string;
  fallbackSubtitle?: string;
}

const FAQ_CATEGORY_LABELS: Record<
  string,
  { [SupportedLang.ja]: string; [SupportedLang.en]: string }
> = {
  "1": {
    [SupportedLang.ja]: "写真の配信方法について",
    [SupportedLang.en]: "About photo delivery methods",
  },
  "2": {
    [SupportedLang.ja]: "配信後の写真について",
    [SupportedLang.en]: "About delivered photos",
  },
  "3": {
    [SupportedLang.ja]: "カメラアプリについて",
    [SupportedLang.en]: "About the camera app",
  },
  "4": {
    [SupportedLang.ja]: "保護者アプリについて",
    [SupportedLang.en]: "About the parent app",
  },
  "5": {
    [SupportedLang.ja]: "保護者の利用プランについて",
    [SupportedLang.en]: "About parent usage plans",
  },
  "6": {
    [SupportedLang.ja]: "アカウント設定について",
    [SupportedLang.en]: "About account settings",
  },
  "7": {
    [SupportedLang.ja]: "園児の入園/卒園・退園/進級について",
    [SupportedLang.en]: "About enrollment, graduation, withdrawal, and promotion",
  },
  "8": {
    [SupportedLang.ja]: "その他",
    [SupportedLang.en]: "Other",
  },
};

const USER_MANUAL_CATEGORY_LABELS: Record<
  string,
  { [SupportedLang.ja]: string; [SupportedLang.en]: string }
> = {
  "8": {
    [SupportedLang.ja]: "その他",
    [SupportedLang.en]: "Other",
  },
};

const Breadcrumbs = ({
  fallbackTitle = "",
  fallbackSubtitle = "",
}: BreadcrumbsProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { classes } = useAllClasses();

  const items = useMemo<BreadcrumbItem[]>(() => {
    const pathName = location.pathname;
    const classMatch = pathName.match(
      /^\/class\/(\d+)(?:\/student(?:\/(\d+|new))?)?$/,
    );
    const pictureClassMatch = pathName.match(/^\/(?:publish|delete)\/(\d+)$/);
    const classId = classMatch?.[1] || pictureClassMatch?.[1];
    const studentId = classMatch?.[2];
    const currentClass = classes.find((c) => c.id === Number(classId));
    const classLabel = currentClass
      ? `${currentClass.year}-${currentClass.name}`
      : t("_header._class_management");

    if (pathName === BASE_PATH) {
      return [{ label: t("_header._home") }];
    }

    if (pathName === UPLOAD_PATH) {
      return [{ label: t("_header._upload") }];
    }

    if (pathName === UNPUBLISHED_PICTURES_PATH) {
      return [{ label: t("_header._unpublished_picture_management") }];
    }

    if (pathName.match(/^\/publish\/\d+$/)) {
      return [
        {
          label: t("_header._unpublished_picture_management"),
          path: UNPUBLISHED_PICTURES_PATH,
        },
        { label: classLabel },
      ];
    }

    if (pathName.match(/^\/delete\/\d+$/)) {
      return [
        {
          label: t("_header._unpublished_picture_management"),
          path: UNPUBLISHED_PICTURES_PATH,
        },
        {
          label: classLabel,
          path: `${UNPUBLISHED_PICTURES_PATH}/${classId}`,
        },
        { label: t("_header._delete_picture_management") },
      ];
    }

    if (pathName === PICTURES_PATH) {
      return [{ label: t("_header._picture_management") }];
    }

    if (pathName === PICTURES_SUMMARIZATION_PATH) {
      return [
        {
          label: t("_published_picture._published_date").replace(
            /[：:]\s*$/,
            "",
          ),
        },
      ];
    }

    if (pathName === PICTURES_RESULTS_PATH) {
      const params = new URLSearchParams(location.search);
      const isPublishedDateSearch =
        params.has("date") || params.get("source") === "publishedDate";
      const label = isPublishedDateSearch
        ? t("_published_picture._published_date").replace(/[：:]\s*$/, "")
        : t("_header._picture_management");
      const path = isPublishedDateSearch
        ? `${PICTURES_SUMMARIZATION_PATH}${
            params.has("date") ? `?date=${params.get("date")}` : ""
          }`
        : PICTURES_PATH;

      return [
        { label, path },
        { label: t("_generic._search_result", { defaultValue: "検索結果" }) },
      ];
    }

    if (pathName === CLASS_PATH) {
      return [{ label: t("_header._class_management") }];
    }

    if (pathName === `${CLASS_PATH}/${NEW}`) {
      return [
        { label: t("_header._class_management"), path: CLASS_PATH },
        { label: t("_class_form._create_class") },
      ];
    }

    if (classMatch) {
      if (!studentId) {
        return [
          { label: t("_header._class_management"), path: CLASS_PATH },
          { label: classLabel },
        ];
      }

      return [
        { label: t("_header._class_management"), path: CLASS_PATH },
        {
          label: classLabel,
          path: `${CLASS_PATH}/${classId}/${STUDENT}`,
        },
        {
          label:
            studentId === NEW
              ? t("_student_form._add_student")
              : t("_student_form._student_information"),
        },
      ];
    }

    if (pathName === ACCOUNT_PATH) {
      return [{ label: t("_header._account_management") }];
    }

    if (pathName === CHANGE_PASSWORD_PATH) {
      return [
        { label: t("_header._account_management"), path: ACCOUNT_PATH },
        { label: t("_change_password._change_password") },
      ];
    }

    if (pathName === `${ACCOUNT_PATH}/${NEW}`) {
      return [
        { label: t("_header._account_management"), path: ACCOUNT_PATH },
        { label: t("_account_form._create_teacher_account") },
      ];
    }

    if (pathName.match(/^\/account\/\d+$/)) {
      return [
        { label: t("_header._account_management"), path: ACCOUNT_PATH },
        { label: t("_account_form._teacher_account") },
      ];
    }

    if (pathName === SYSTEM_INFO_PATH) {
      return [{ label: t("_header._notification") }];
    }

    if (pathName === FACILITY_PATH) {
      return [{ label: t("_header._facility_information") }];
    }

    if (pathName === SETTING_PATH) {
      return [{ label: t("_header._setting") }];
    }

    if (pathName.startsWith(USER_MANUAL_PATH)) {
      const baseItem = {
        label: t("_header._user_manual"),
        path: USER_MANUAL_PATH,
      };
      const categoryId = pathName.replace(`${USER_MANUAL_PATH}/`, "");
      const manual = USER_MANUAL_LINKS.find(
        (item) => item.category_id === Number(categoryId),
      );
      const manualCategory = USER_MANUAL_CATEGORY_LABELS[categoryId];
      const detailLabel =
        i18n.language === SupportedLang.ja
          ? manual?.category_ja || manualCategory?.[SupportedLang.ja]
          : manual?.category_en || manualCategory?.[SupportedLang.en];

      return pathName === USER_MANUAL_PATH
        ? [{ label: t("_header._user_manual") }]
        : [
            baseItem,
            {
              label:
                detailLabel || t("_generic._details", { defaultValue: "詳細" }),
            },
          ];
    }

    if (pathName.startsWith(FAQ_PATH)) {
      const baseItem = { label: t("_header._faq"), path: FAQ_PATH };
      const categoryId = pathName.replace(`${FAQ_PATH}/`, "");
      const category = FAQ_CATEGORY_LABELS[categoryId];
      const detailLabel =
        i18n.language === SupportedLang.ja
          ? category?.[SupportedLang.ja]
          : category?.[SupportedLang.en];

      return pathName === FAQ_PATH
        ? [{ label: t("_header._faq") }]
        : [
            baseItem,
            {
              label:
                detailLabel || t("_generic._details", { defaultValue: "詳細" }),
            },
          ];
    }

    if (pathName === PARENTS_MATERIAL_PATH) {
      return [{ label: t("_header._parents_material") }];
    }

    return [];
  }, [classes, i18n.language, location.pathname, location.search, t]);

  if (items.length === 0) {
    if (!fallbackTitle) return null;

    return (
      <h1 className="flex min-w-0 items-center gap-2 overflow-hidden text-lg font-semibold leading-tight text-[#333333]">
        <span className="truncate">{fallbackTitle}</span>
        {fallbackSubtitle ? (
          <>
            <span className="shrink-0 text-[#9B9B9B]">/</span>
            <span className="truncate text-base text-[#555555]">
              {fallbackSubtitle}
            </span>
          </>
        ) : null}
      </h1>
    );
  }

  return (
    <nav aria-label="Breadcrumb" className="min-w-0 text-lg font-semibold">
      <ol className="flex min-w-0 flex-wrap items-center gap-3 text-[#333333]">
        <li className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#D9D9D9] bg-white">
          <Link
            to={BASE_PATH}
            aria-label={t("_header._home")}
            className="flex h-full w-full items-center justify-center transition-colors duration-200 hover:text-primary"
          >
            <HomeIcon width="20" height="20" color="currentColor" />
          </Link>
        </li>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li
              key={`${item.label}-${index}`}
              className="flex min-w-0 items-center gap-3"
            >
              <span className="shrink-0 text-2xl font-normal text-[#555555]">
                /
              </span>
              {item.path && !isLast ? (
                <Link
                  to={item.path}
                  className="truncate transition-colors duration-200 hover:text-primary"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="truncate">{item.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
